/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-25 10:07:54
 *
 *******************************************************/
import React from 'react';
import { Field } from 'redux-form';
import {
  FormLayout,
  SkeletonDisplayText,
  SkeletonBodyText,
  Button,
  Icon,
  TextStyle
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { EditMinor } from '@shopify/polaris-icons';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { normalizeNumberWithLimit } from 'src/helpers/form-validation-helper';
import { MAX_NUMBER_SEARCH_RESULT_PER_PAGE } from 'src/constants/search-page-display';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';
import BannerCustom from 'src/components/views/common/banner-custom';

const SearchResultTab = props => {
  const { i18n, loading, collectionsActive, pagesActive, updateField, allowSearchV2, hasThemeV3 } =
    props;

  /**
   * Handle blur input
   * @param {*} event
   * @param {*} value
   * @param {*} name
   */
  const onBlurTextField = (event, value, name) => {
    if (!value) {
      event.preventDefault();
      updateField(name, '1');
    }
  };
  return loading ? (
    <div>
      <FormLayout>
        <SkeletonBodyText lines={1} />
        <SkeletonDisplayText size="small" />
        <SkeletonBodyText lines={1} />
        <SkeletonBodyText lines={1} />
        <SkeletonBodyText lines={1} />
      </FormLayout>
    </div>
  ) : (
    <div className="search-results-tab">
      <FormLayout>
        <p>{i18n.translate('AdminHTML.SearchPageDisplay.SearchResultsPage.description')}</p>
        <p style={{ fontWeight: 600 }}>
          {i18n.translate('AdminHTML.SearchPageDisplay.SearchResultsPage.product_Title')}
        </p>
        <Field
          name="products.label"
          component={RenderTextField}
          label={i18n.translate(
            'AdminHTML.SearchPageDisplay.SearchResultsPage.product_Input1_Label'
          )}
        />
        <Field
          name="collections.active"
          component={RenderToggleSwitch}
          label={i18n.translate('AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Title')}
        />
        {collectionsActive && (
          <div className="groupFields">
            <div className="groupFields-flex-2">
              <Field
                name="collections.label"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Input1_Label'
                )}
              />
              <Field
                name="collections.pageSize"
                component={RenderTextField}
                type="number"
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Input2_Label'
                )}
                onBlur={(event, value) => onBlurTextField(event, value, 'collections.pageSize')}
                normalize={(value, prevValue) =>
                  normalizeNumberWithLimit(value, prevValue, MAX_NUMBER_SEARCH_RESULT_PER_PAGE)
                }
              />
            </div>
            <div className="groupFields-flex-2">
              <Field
                name="collections.displayImage"
                component={RenderCheckbox}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Checkbox1_Label'
                )}
              />
              <div style={{ display: 'flex' }}>
                <Field
                  name="collections.displayDescription"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Checkbox2_Label'
                  )}
                />
                <RenderTooltip
                  content={i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.collection_Checkbox2_Tooltip'
                  )}
                  icon={true}
                />
              </div>
            </div>
          </div>
        )}
        <Field
          name="pages.active"
          component={RenderToggleSwitch}
          label={i18n.translate('AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Title')}
        />
        {pagesActive && (
          <div className="groupFields">
            <div className="groupFields-flex-2">
              <Field
                name="pages.label"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Input1_Label'
                )}
              />
              <Field
                name="pages.pageSize"
                component={RenderTextField}
                type="number"
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Input2_Label'
                )}
                onBlur={(event, value) => onBlurTextField(event, value, 'pages.pageSize')}
                normalize={(value, prevValue) =>
                  normalizeNumberWithLimit(value, prevValue, MAX_NUMBER_SEARCH_RESULT_PER_PAGE)
                }
              />
            </div>
            <div className="groupFields-flex-2">
              <Field
                name="pages.displayImage"
                component={RenderCheckbox}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Checkbox1_Label'
                )}
              />
              <div style={{ display: 'flex' }}>
                <Field
                  name="pages.displayExcerpt"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Checkbox2_Label'
                  )}
                />
                <RenderTooltip
                  content={i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.blog_Checkbox2_Tooltip'
                  )}
                  icon={true}
                />
              </div>
            </div>
          </div>
        )}
        {allowSearchV2 && (
          <>
            {/* HIDE => requirement is pending */}
            {false && hasThemeV3 && (
              <div className="layout-custom">
                <p style={{ fontWeight: 600 }}>
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.layoutCustom_title'
                  )}
                </p>
                <p>
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.layoutCustom_subtext'
                  )}
                </p>
                <Button>
                  <Icon color="sub" source={EditMinor} />
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.SearchResultsPage.layoutCustomAction_label'
                  )}
                </Button>
              </div>
            )}

            <BannerCustom
              idDismiss="iswWithResultTabCustomBanner"
              title={i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.bannerTitle')}
              action={() => history.push(routes.merchandising.view())}
              actionContent={i18n.translate(
                'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerAction'
              )}>
              <p>
                {i18n.translate('AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t1'
                      )}
                    </TextStyle>
                  ),
                  t2: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t2'
                      )}
                    </TextStyle>
                  ),
                  t3: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.InstantSearchDisplay.ISWWithResults.bannerContent_t3'
                      )}
                    </TextStyle>
                  )
                })}
              </p>
            </BannerCustom>
          </>
        )}
      </FormLayout>
    </div>
  );
};
export default withI18n()(SearchResultTab);
